import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxComponent, NzCheckboxModule } from 'ng-zorro-antd/checkbox';

import { NzDividerModule } from 'ng-zorro-antd/divider';
import {
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzRadioGroupComponent, NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import {
  BehaviorSubject,
  finalize,
  from,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { ApiResponse } from '../../../../../core/models/api-response.models';
import {
  ApiResponses,
  BehaviourListModel,
  BehaviourLookUpModel,
  BehaviourSubListModel,
} from '../../../../../core/models/common.models';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { LoaderService } from '../../../../../core/services/loader.service';
import { OrderByPipe } from '../../../../../shared/pipes/order-by.pipe';
import { CommonService } from '../../../../../shared/services/common.service';
import {
  InsightMode,
  BehaviorFrequency,
  CustomQuestionReportType,
} from '../../../insights.enums';
import {
  BehaviorDetailModel,
  BehaviorModel,
  caseBehaviorConsequenceDescriptions,
  caseBehaviorTypeDescriptions,
  CaseDetailModel,
  EditBehaviorDrawerParams,
} from '../../../insights.interface';
import { BehaviorService } from './define-behavior.service';
import _ from 'lodash';
import { PrintCaseBehaviorComponent } from '../print-case-behavior/print-case-behavior.component';
import { OrganizationModel } from '../../../../organizations/organization.interface';
import { OrganizationService } from '../../../../organizations/organization.service';
import { CustomQuestionService } from '../../../../manage-settings/custom-question/custom-question.service';
import {
  behaviorGoals,
  QuestionItem,
} from '../../../../manage-settings/manage-settings.interface';
import { ManageSettingsService } from '../../../../manage-settings/manage-settings.service';
import behaviorGoalExamples from './goalsExample.json';
import { behaviorDefinition } from './behaviorDefinition';
import { BipSummaryComponent } from '../bip-summary/bip-summary.component';
import { BIP_SUMMARY_DATA } from '../bip-summary/bip-summary-example';

@Component({
  selector: 'app-define-behavior',
  standalone: true,
  imports: [
    NzFormModule,
    SvgIconComponent,
    NzDividerModule,
    NzButtonModule,
    NzRadioGroupComponent,
    NzRadioModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzCheckboxModule,
    NzCheckboxComponent,
    NzInputModule,
    NzModalModule,
    CommonModule,
    NzSpinModule,
    ReactiveFormsModule,
    FormsModule,
    NzToolTipModule,
    OrderByPipe,
    BipSummaryComponent,
  ],
  templateUrl: './define-behavior.component.html',
  styleUrl: './define-behavior.component.scss',
})
export class DefineBehaviorComponent implements OnInit, OnDestroy {
  isVisible = false;
  isFunctionalEquivaluentModelVisible = false;
  isSettingEventModelVisible = false;
  isGoalExampleModelVisible = false;
  behaviorGoalExamples = behaviorGoalExamples;
  currentPage: number;
  totalPages = 12;
  behvaiorSummaryData = BIP_SUMMARY_DATA;
  caseParams: EditBehaviorDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.ADD_BEHAVIOR,
    behaviorId: undefined,
  };
  isPrintLoading = false;
  behaviorGoal: behaviorGoals | null = null;
  private fb = inject(FormBuilder);
  private readonly formControls = {
    caseId: [null, [Validators.required]],
    behaviorCategoryId: [null, [Validators.required]],
    caseBehaviorTypeIds: [[], [Validators.required]],
    operationalDefinition: [
      '',
      [Validators.required, Validators.maxLength(4000)],
    ],
    behaviourFrequencyId: [null, [Validators.required]],
    behaviourFrequencyCount: [
      { value: null, disabled: true },
      [
        Validators.required,
        Validators.pattern('^[0-9]*$'),
        Validators.maxLength(3),
      ],
    ],
    isDurationRequired: [
      { value: null, disabled: true },
      [Validators.required],
    ],
    duration: [null, [Validators.pattern('^[0-9]*$'), Validators.maxLength(3)]],
    behaviourIntensityId: [null, [Validators.required]],
    goals: ['', [Validators.required, Validators.maxLength(4000)]],
    draft: [true],
    draftStep: [1],
    caseBehaviorAntecedentIds: [[], [Validators.required]],
    caseBehaviorConsequenceIds: [[], [Validators.required]],
    caseBehaviorEnvironmentIds: [[], [Validators.required]],
    caseBehaviorSettingEventIds: [[], [Validators.required]],
    caseBehaviorTypeDescriptions: this.fb.array([]),
    caseBehaviorConsequenceDescription: this.fb.array([]),
    caseBehaviorAntecedentDescription: this.fb.array([]),
    caseBehaviorCustomQuestionAnswer: this.fb.array([]),
  };

  form: FormGroup;
  behaviorCategoryList: BehaviourListModel[] = [];
  behaviourSubCategoryList: BehaviourSubListModel[] = [];
  behaviourFrequencyList: BehaviourListModel[] = [];
  behaviourIntensityList: BehaviourListModel[] = [];
  behaviourEnvironmentList: BehaviourListModel[] = [];
  behaviourSettingEventList: BehaviourListModel[] = [];
  behaviourAntecedentList: BehaviourListModel[] = [];
  behaviorConsequenceList: BehaviourListModel[] = [];
  customQuestionList: QuestionItem[] = [];
  loading$: Observable<boolean>;
  loading = false;
  saveLaterLoading = false;
  private nextLoadingSubject = new BehaviorSubject<boolean>(false);
  nextLoading$ = this.nextLoadingSubject.asObservable();
  behaviorDetail?: BehaviorDetailModel | null;
  pageTitles: { title: string; controls: string[]; tooltip?: string }[] = [
    {
      title: 'Category',
      controls: ['caseId', 'behaviorCategoryId'],
      tooltip: 'Select behavior category',
    },
    {
      title: 'Behavior Types',
      controls: ['caseBehaviorTypeIds', 'caseBehaviorTypeDescriptions'],
      tooltip: 'Choose behavior types and descriptions',
    },
    {
      title: 'Operational Definition',
      controls: ['operationalDefinition'],
      tooltip: 'Define operational behavior characteristics',
    },
    {
      title: 'Frequency',
      controls: [
        'behaviourFrequencyId',
        'behaviourFrequencyCount',
        'isDurationRequired',
        'duration',
      ],
      tooltip: 'Specify behavior frequency and duration',
    },
    {
      title: 'Intensity',
      controls: ['behaviourIntensityId'],
      tooltip: 'Indicate behavior intensity level',
    },
    {
      title: 'Environments',
      controls: ['caseBehaviorEnvironmentIds'],
      tooltip: 'Select relevant environments',
    },
    {
      title: 'Setting Events',
      controls: ['caseBehaviorSettingEventIds'],
      tooltip: 'Choose applicable setting events',
    },
    {
      title: 'Antecedents',
      controls: [
        'caseBehaviorAntecedentIds',
        'caseBehaviorAntecedentDescription',
      ],
      tooltip: 'Identify behavior antecedents',
    },
    {
      title: 'Consequences',
      controls: [
        'caseBehaviorConsequenceIds',
        'caseBehaviorConsequenceDescription',
      ],
      tooltip: 'Specify behavior consequences',
    },
    {
      title: 'Goals',
      controls: ['goals'],
      tooltip: 'Define behavior goals',
    },
    {
      title: 'Questions',
      controls: ['caseBehaviorCustomQuestionAnswer'],
      tooltip: 'Answer custom questions',
    },
    {
      title: 'Preview',
      controls: [],
      tooltip: 'Preview all entered information',
    },
  ];
  isPreviewPage = false;
  selectedCategory?: number;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private drawerService: DrawerService,
    private commonService: CommonService,
    private loaderService: LoaderService,
    private behaviorService: BehaviorService,
    private cd: ChangeDetectorRef,
    private viewContainerRef: ViewContainerRef,
    private injector: Injector,
    private orgService: OrganizationService,
    private customQuestionService: CustomQuestionService,
    private manageSettingService: ManageSettingsService
  ) {
    this.currentPage = 1;
    this.loading$ = this.loaderService.loading$;
    this.form = this.fb.group(this.formControls);
    this.setupDrawerSubscription();
  }

  async ngOnInit() {
    setTimeout(async () => {
      await this.getMasterData();
    }, 500);
    this.form.controls['behaviorCategoryId'].valueChanges.subscribe(
      (val: number) => {
        const operationalDefCtrl = this.form.controls['operationalDefinition'];
        if (operationalDefCtrl.value === '') {
          const found = behaviorDefinition.find(item => item.id === val);
          if (found && found.definition) {
            this.form.patchValue({
              operationalDefinition: found.definition,
            });
          }
        }

        if (this.selectedCategory !== val) {
          this.selectedCategory = val;
          this.behaviourSubCategoryList = [];
          this.form.controls['caseBehaviorTypeIds'].setValue([]);
          (
            this.form.controls['caseBehaviorTypeDescriptions'] as FormArray
          ).clear();
          if (val) {
            this.getBehaviourSubTypes(val);
          }
        }
      }
    );
    this.form.controls['behaviourFrequencyId'].valueChanges.subscribe(
      (val: number) => {
        if (val) {
          this.form.controls['behaviourFrequencyCount'].enable();
          this.form.controls['isDurationRequired'].enable();
        } else {
          this.form.controls['behaviourFrequencyCount'].setValue(null);
          this.form.controls['isDurationRequired'].setValue(null);
          this.form.controls['behaviourFrequencyCount'].disable();
          this.form.controls['isDurationRequired'].disable();
        }
      }
    );
    this.form.controls['isDurationRequired'].valueChanges.subscribe(
      (val: boolean) => {
        if (val === true) {
          this.form.controls['duration'].enable();
          this.form.controls['duration'].setValidators([
            Validators.required,
            Validators.pattern('^[0-9]*$'),
            Validators.maxLength(3),
          ]);
          this.form.controls['duration'].updateValueAndValidity();
        } else {
          this.form.controls['duration'].setValue(null);
          this.form.controls['duration'].setValidators(null);
          this.form.controls['duration'].updateValueAndValidity();
        }
      }
    );
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setNextLoading(loading: boolean): void {
    this.nextLoadingSubject.next(loading);
  }

  getMasterData() {
    from(
      Promise.all([
        this.commonService.getBehaviourLookups().toPromise(),
        this.customQuestionService
          .getQuestionsByOrg(this.caseParams?.case?.student.organizationId)
          .toPromise(),
        this.manageSettingService
          .getbehaviorGoalsByOrgId(
            this.caseParams?.case?.student.organizationId
          )
          .toPromise(),
      ])
    )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (this.caseParams.mode === InsightMode.ADD_BEHAVIOR) {
            this.loaderService.hide();
          }
        })
      )
      .subscribe(
        ([response, queResponse, behvaiorGoalsRes]: [
          ApiResponse<BehaviourLookUpModel> | undefined,
          ApiResponses<QuestionItem> | undefined,
          ApiResponse<behaviorGoals> | undefined,
        ]) => {
          if (response?.result) {
            this.behaviorCategoryList = response.result.behaviorCategory;
            this.behaviourFrequencyList = response.result.behaviourFrequency;
            this.behaviourIntensityList = response.result.behaviourIntensity;
            this.behaviourEnvironmentList =
              response.result.behaviourEnvironment;
            this.behaviourSettingEventList =
              response.result.behaviourSettingEvent;
            this.behaviourAntecedentList = response.result.behaviourAntecedent;
            this.behaviorConsequenceList = response.result.behaviorConsequence;
          }
          this.customQuestionList = queResponse?.result
            ? _.sortBy(queResponse.result, 'sequenceNumber')
            : [];
          if (this.customQuestionList.length > 0) {
            this.customQuestionList.forEach(que => {
              const queRecord =
                this.behaviorDetail?.caseBehaviorCustomQuestionAnswer.find(
                  q => q.customQuestionId === que.id
                );

              const customQuestionGrp = this.fb.group({
                customQuestionId: [que.id],
                answer: [
                  queRecord ? queRecord.answer : '',
                  que.isRequired
                    ? [Validators.required, Validators.maxLength(500)]
                    : [Validators.maxLength(500)],
                ],
              });
              this.customQuestions.push(customQuestionGrp);
            });
          }
          this.behaviorGoal = behvaiorGoalsRes ? behvaiorGoalsRes.result : null;
          if (
            this.caseParams.mode === InsightMode.ADD_BEHAVIOR &&
            this.behaviorGoal?.statusId === 1
          ) {
            this.form.patchValue({
              goals: this.behaviorGoal?.goalTemplateText,
            });
          }

          this.cd.detectChanges();
        }
      );
  }

  async getBehaviourSubTypes(behaviourId: number) {
    this.commonService
      .getBehaviourSubTypes(behaviourId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiResponses<BehaviourSubListModel>) => {
          this.behaviourSubCategoryList = res.result;
          this.cd.detectChanges();
          this.loaderService.hide();
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  getSelectedBehaviorSubCategory() {
    const selected = this.form.controls['caseBehaviorTypeIds'].value;
    return (
      this.behaviourSubCategoryList.filter(res => selected.includes(res.id)) ||
      []
    );
  }

  getBehaviorFrequencyLabel(name: string) {
    return BehaviorFrequency[name as keyof typeof BehaviorFrequency];
  }

  getBehvaiorGoalLabel(categoryId: number) {
    const behvaiorCategory = this.behaviorCategoryList.find(
      cat => cat.id === categoryId
    );
    const behaviorGoalExamples = this.behaviorGoalExamples.behavioral_goals;
    if (behvaiorCategory) {
      const goalExample = behaviorGoalExamples.find(
        example =>
          example.goal.toLocaleLowerCase() ===
          behvaiorCategory.name.toLocaleLowerCase()
      );
      if (goalExample) {
        return goalExample.description;
      }
      return 'Add details';
    }
    return 'Add details';
  }

  private setupDrawerSubscription(): void {
    this.loaderService.show();
    this.drawerService
      .getParams<EditBehaviorDrawerParams>(DRAWER_IDS.DEFINE_BEHAVIOR)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          if (params.mode === InsightMode.ADD_BEHAVIOR) {
            this.form.get('caseId')?.setValue(this.caseParams.case?.id);
          } else {
            this.getBehaviorById(this.caseParams.behaviorId);
          }
        }
      });
  }

  getBehaviorById(id: number | undefined) {
    if (id) {
      this.behaviorService
        .getBehaviorById(id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async (response: ApiResponse<BehaviorDetailModel>) => {
            if (response.result) {
              this.behaviorDetail = response.result;
              const caseBehaviorTypeIds = this.behaviorDetail[
                'caseBehaviorTypes'
              ].map(e => e.behaviorTypeId);
              const caseBehaviorEnvironmentIds = this.behaviorDetail[
                'caseBehaviorEnvironments'
              ].map(e => e.behaviorEnvironmentId);
              const caseBehaviorConsequenceIds = this.behaviorDetail[
                'caseBehaviorConsequences'
              ].map(e => e.behaviorConsequenceId);
              const caseBehaviorAntecedentIds = this.behaviorDetail[
                'caseBehaviorAntecedents'
              ].map(e => e.behaviorAntecedentId);
              const caseBehaviorSettingEventIds = this.behaviorDetail[
                'caseBehaviorSettingEvents'
              ].map(e => e.behaviorSettingEventId);
              const caseBehaviorTypeDescriptions = this.behaviorDetail[
                'caseBehaviorTypeDescriptions'
              ].map(e => {
                const caseBehaviorOther = this.fb.group({
                  behaviorTypeId: [e.behaviorTypeId],
                  description: [
                    '',
                    [Validators.required, Validators.maxLength(500)],
                  ],
                });
                this.behaviorTypeDescription.push(caseBehaviorOther);
                return {
                  behaviorTypeId: e.behaviorTypeId,
                  description: e.description,
                };
              });
              const caseBehaviorConsequenceDescription = this.behaviorDetail[
                'caseBehaviorConsequenceDescriptions'
              ].map(e => {
                const consequenceOther = this.fb.group({
                  behaviorConsequenceId: [e.behaviorConsequenceId],
                  description: [
                    '',
                    [Validators.required, Validators.maxLength(500)],
                  ],
                });
                this.consequenceDescription.push(consequenceOther);
                return {
                  behaviorConsequenceId: e.behaviorConsequenceId,
                  description: e.description,
                };
              });
              const caseBehaviorAntecedentDescription =
                this.behaviorDetail['caseBehaviorAntecedentDescriptions']?.map(
                  e => {
                    const antecedentOther = this.fb.group({
                      behaviorAntecedentId: [e.behaviorAntecedentId],
                      description: [
                        '',
                        [Validators.required, Validators.maxLength(500)],
                      ],
                    });
                    this.antecedentDescription.push(antecedentOther);
                    return {
                      behaviorAntecedentId: e.behaviorAntecedentId,
                      description: e.description,
                    };
                  }
                ) || [];
              let operationalDefinition = '';
              const found = behaviorDefinition.find(
                item => item.id === this.behaviorDetail?.behaviorCategoryId
              );
              if (found && found.definition) {
                operationalDefinition =
                  this.behaviorDetail.operationalDefinition || found.definition;
              }

              const formData = {
                ...this.behaviorDetail,
                caseBehaviorTypeIds,
                caseBehaviorEnvironmentIds,
                caseBehaviorAntecedentIds,
                caseBehaviorSettingEventIds,
                caseBehaviorConsequenceIds,
                caseBehaviorTypeDescriptions,
                caseBehaviorConsequenceDescription,
                caseBehaviorAntecedentDescription,
                operationalDefinition,
              };

              this.getBehaviourSubTypes(this.behaviorDetail.behaviorCategoryId);
              this.selectedCategory = this.behaviorDetail.behaviorCategoryId;

              await this.form.patchValue(formData);

              this.form
                .get('isDurationRequired')
                ?.setValue(!!this.behaviorDetail.duration);
              this.form.markAllAsTouched();
              this.currentPage =
                this.caseParams.mode === InsightMode.VIEW_BEHAVIOR
                  ? 12
                  : this.behaviorDetail.draftStep || 1;
              this.managePreview();
            }
          },
          error: error => console.error(error),
        });
    }
  }
  onCheckboxChange(ctrl: string, checked: boolean, value: number): void {
    this.form.get(ctrl)?.markAsTouched();
    const selectedOptions = this.form.get(ctrl)?.value || [];
    if (checked) {
      selectedOptions.push(value);
    } else {
      const index = selectedOptions.indexOf(value);
      if (index > -1) {
        selectedOptions.splice(index, 1);
      }
    }
    this.form.get(ctrl)?.setValue(selectedOptions);
  }

  get behaviorTypeDescription(): FormArray {
    return this.form.controls['caseBehaviorTypeDescriptions'] as FormArray;
  }

  get consequenceDescription(): FormArray {
    return this.form.controls[
      'caseBehaviorConsequenceDescription'
    ] as FormArray;
  }

  get antecedentDescription(): FormArray {
    return this.form.controls['caseBehaviorAntecedentDescription'] as FormArray;
  }

  get customQuestions(): FormArray {
    return this.form.controls['caseBehaviorCustomQuestionAnswer'] as FormArray;
  }

  get customQuestionsControls(): FormGroup[] {
    return this.customQuestions?.controls as FormGroup[];
  }

  get behaviorTypeDescriptionControls(): FormGroup[] {
    return this.behaviorTypeDescription?.controls as FormGroup[];
  }

  get consequenceDescriptionControls(): FormGroup[] {
    return this.consequenceDescription?.controls as FormGroup[];
  }

  get antecedentDescriptionControls(): FormGroup[] {
    return this.antecedentDescription?.controls as FormGroup[];
  }

  getBehaviorTypeDescriptionTitle(id: number): string {
    const item = this.behaviourSubCategoryList.find(c => c.id === id);
    return item ? item.name : '';
  }

  getBehaviorConsequenceTitle(id: number): string {
    const item = this.behaviorConsequenceList.find(c => c.id === id);
    return item ? item.name : '';
  }

  getBehaviorAntecedentTitle(id: number): string {
    const item = this.behaviourAntecedentList.find(c => c.id === id);
    return item ? item.name : '';
  }

  onBehaviorTypeChange(checked: boolean, category: BehaviourSubListModel) {
    if (checked && category.isOther) {
      const caseBehaviorOther = this.fb.group({
        behaviorTypeId: [category.id],
        description: ['', [Validators.required, Validators.maxLength(500)]],
      });
      this.behaviorTypeDescription.push(caseBehaviorOther);
    } else if (category.isOther && !checked) {
      const index = this.behaviorTypeDescription.value.findIndex(
        (v: caseBehaviorTypeDescriptions) => v.behaviorTypeId === category.id
      );
      if (index !== -1) {
        this.behaviorTypeDescription.removeAt(index);
      }
    }
  }

  onControlInputChange(event: Event, control: AbstractControl | null): void {
    const input = event.target as HTMLTextAreaElement;
    const value = input.value;
    if (/^\s+$/.test(value)) {
      input.value = '';
      control?.setValue('');
    }
  }

  onConsequenceChange(checked: boolean, category: BehaviourListModel) {
    if (checked && category.isOther) {
      const consequenceOther = this.fb.group({
        behaviorConsequenceId: [category.id],
        description: ['', [Validators.required, Validators.maxLength(500)]],
      });
      this.consequenceDescription.push(consequenceOther);
    } else if (category.isOther && !checked) {
      const index = this.consequenceDescription.value.findIndex(
        (v: caseBehaviorConsequenceDescriptions) =>
          v.behaviorConsequenceId === category.id
      );
      if (index !== -1) {
        this.consequenceDescription.removeAt(index);
      }
    }
  }

  onAntecedentChange(checked: boolean, category: BehaviourListModel) {
    if (checked && (category.name === 'Other' || category.id === 20)) {
      const antecedentOther = this.fb.group({
        behaviorAntecedentId: [category.id],
        description: ['', [Validators.required, Validators.maxLength(500)]],
      });
      this.antecedentDescription.push(antecedentOther);
    } else if (!checked && (category.name === 'Other' || category.id === 20)) {
      const index = this.antecedentDescription.value.findIndex(
        (v: { behaviorAntecedentId: number }) =>
          v.behaviorAntecedentId === category.id
      );
      if (index !== -1) {
        this.antecedentDescription.removeAt(index);
      }
    }
  }

  currentControls() {
    const controlNames = this.pageTitles[this.currentPage - 1].controls;
    return controlNames.map(control => this.form.get(control));
  }

  checkCurrentPageValidations() {
    return this.currentControls().every(control => control?.valid);
  }

  showWarningModal(): void {
    this.isVisible = true;
  }
  showBehaviorDefinedModal(): void {
    this.isVisible = true;
  }
  handleOk(): void {
    this.isVisible = false;
  }
  handleCancel(): void {
    this.isVisible = false;
  }
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.showWarningModal();
    }
  }

  managePreview(isUpdateDetail = false) {
    if (this.currentPage === this.totalPages) {
      this.form.disable();
      this.isPreviewPage = true;
      if (isUpdateDetail) {
        const formData = this.form.getRawValue();

        this.behaviorDetail = {
          ...formData,
          behaviorCategoryId: formData.behaviorCategoryId,
          behaviorCategoryName: _.get(
            _.find(this.behaviorCategoryList, {
              id: formData.behaviorCategoryId,
            }),
            'name',
            ''
          ),
          operationalDefinition: formData.operationalDefinition,
          behaviourFrequencyId: formData.behaviourFrequencyId,
          behaviourFrequencyName: _.get(
            _.find(this.behaviourFrequencyList, {
              id: formData.behaviourFrequencyId,
            }),
            'name',
            ''
          ),
          behaviourFrequencyCount: formData.behaviourFrequencyCount,
          duration: formData.duration,
          behaviourIntensityId: formData.behaviourIntensityId,
          behaviourIntensityName: _.get(
            _.find(this.behaviourIntensityList, {
              id: formData.behaviourIntensityId,
            }),
            'name',
            ''
          ),
          goals: formData.goals,
          caseBehaviorTypes: _.filter(this.behaviourSubCategoryList, item =>
            _.includes(formData.caseBehaviorTypeIds, item.id)
          ).map(item => ({
            behaviorTypeId: item.id,
            behaviorTypeName: item.name,
          })),
          caseBehaviorAntecedents: _.filter(
            this.behaviourAntecedentList,
            item => _.includes(formData.caseBehaviorAntecedentIds, item.id)
          ).map(item => ({
            behaviorAntecedentId: item.id,
            behaviorAntecedentName: item.name,
          })),
          caseBehaviorConsequences: _.filter(
            this.behaviorConsequenceList,
            item => _.includes(formData.caseBehaviorConsequenceIds, item.id)
          ).map(item => ({
            behaviorConsequenceId: item.id,
            behaviorConsequenceName: item.name,
          })),
          caseBehaviorEnvironments: _.filter(
            this.behaviourEnvironmentList,
            item => _.includes(formData.caseBehaviorEnvironmentIds, item.id)
          ).map(item => ({
            behaviorEnvironmentId: item.id,
            behaviorEnvironmentName: item.name,
          })),
          caseBehaviorSettingEvents: _.filter(
            this.behaviourSettingEventList,
            item => _.includes(formData.caseBehaviorSettingEventIds, item.id)
          ).map(item => ({
            behaviorSettingEventId: item.id,
            behaviorSettingEventName: item.name,
          })),
          caseBehaviorTypeDescriptions:
            formData.caseBehaviorTypeDescriptions.map(
              (desc: caseBehaviorTypeDescriptions) => ({
                behaviorTypeId: desc.behaviorTypeId,
                description: desc.description,
              })
            ),
          caseBehaviorConsequenceDescriptions:
            formData.caseBehaviorConsequenceDescription.map(
              (desc: caseBehaviorConsequenceDescriptions) => ({
                behaviorConsequenceId: desc.behaviorConsequenceId,
                description: desc.description,
              })
            ),
          caseBehaviorAntecedentDescriptions:
            formData.caseBehaviorAntecedentDescription.map(
              (desc: {
                behaviorAntecedentId: number;
                description: string;
              }) => ({
                behaviorAntecedentId: desc.behaviorAntecedentId,
                description: desc.description,
              })
            ),
          caseBehaviorCustomQuestionAnswer:
            formData.caseBehaviorCustomQuestionAnswer.map(
              (ans: { customQuestionId: number; answer: string }) => ({
                customQuestionId: ans.customQuestionId,
                customQuestion:
                  this.customQuestionList.find(
                    q => q.id === ans.customQuestionId
                  )?.customQuestion || '',
                answer: ans.answer,
              })
            ),
        } as BehaviorDetailModel;
      }
    } else if (this.currentPage !== this.totalPages && this.isPreviewPage) {
      this.form.enable();
      this.isPreviewPage = false;
    }
  }

  redirectToStep(event: number) {
    this.currentPage = event;
    this.managePreview();
  }

  saveChanges() {
    if (this.form.get('draft')?.value) {
      this.form.get('draftStep')?.setValue(this.currentPage);
      if (
        this.caseParams.mode === InsightMode.EDIT_BEHAVIOR ||
        this.caseParams.mode === InsightMode.VIEW_BEHAVIOR
      ) {
        this.setNextLoading(true);
        this.behaviorService
          .editBehavior(
            this.form.getRawValue(),
            Number(this?.caseParams?.behaviorId)
          )
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => {
              this.setNextLoading(false);
            })
          )
          .subscribe(() => {
            this.setNextLoading(false);
          });
      } else {
        this.setNextLoading(true);
        this.behaviorService
          .createBehavior(this.form.getRawValue())
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => {
              this.setNextLoading(false);
            })
          )
          .subscribe(res => {
            this.caseParams.behaviorId = res?.result.id;
            this.caseParams.mode = InsightMode.EDIT_BEHAVIOR;
            this.setNextLoading(false);
          });
      }
    }
  }

  checkValidation(control: AbstractControl | null): boolean {
    if (!control) return false;
    if (control.disabled) {
      const errors = control.validator?.(control);
      return errors?.['required'];
    }
    return control.valid;
  }

  checkFormGroupValidity(): boolean {
    let isValid = true;

    Object.keys(this.form.controls).forEach(controlName => {
      const control = this.form.get(controlName);
      if (control?.disabled) {
        control.enable({ emitEvent: false });
      }
    });

    isValid = this.form.valid;

    Object.keys(this.form.controls).forEach(controlName => {
      const control = this.form.get(controlName);
      if (control?.enabled) {
        control.disable({ emitEvent: false });
      }
    });

    return isValid;
  }

  saveForLater() {
    this.form.get('draftStep')?.setValue(this.currentPage);
    this.form.get('draft')?.setValue(true);
    this.saveLaterLoading = true;
    if (
      this.form.value.draft &&
      !!this.form.value.behaviorCategoryId &&
      !!this.form.value.caseId
    ) {
      this.saveBehavior(this.form.value, true);
    }
  }

  onSubmit() {
    this.form.get('draftStep')?.setValue(this.currentPage);
    this.form.get('draft')?.setValue(false);
    if (!this.checkFormGroupValidity()) {
      return;
    }
    this.loading = true;
    this.saveBehavior(this.form.getRawValue(), false);
  }

  saveBehavior(formData: BehaviorModel, isDraft: boolean) {
    const operation$ =
      this.caseParams.mode === InsightMode.EDIT_BEHAVIOR ||
      this.caseParams.mode === InsightMode.VIEW_BEHAVIOR
        ? this.behaviorService.editBehavior(
            formData,
            Number(this?.caseParams?.behaviorId)
          )
        : this.behaviorService.createBehavior(formData);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.saveLaterLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.handleSubmissionSuccess(isDraft);
        },
        error: (error: Error) => {
          this.handleError(error);
        },
      });
  }

  private handleSubmissionSuccess(isDraft: boolean): void {
    this.showNotification(isDraft);
    this.closeDrawer(true);
  }

  private showNotification(isDraft: boolean): void {
    this.commonService.notify(
      'success',
      isDraft
        ? 'Behavior Draft has been saved successfully'
        : this.caseParams.mode === InsightMode.EDIT_BEHAVIOR ||
            this.caseParams.mode === InsightMode.VIEW_BEHAVIOR
          ? 'Behavior has been updated successfully'
          : 'Behavior has been created successfully',
      '',
      '450px'
    );
  }

  private handleError(error: Error): void {
    this.form.get('draft')?.setValue(true);
    this.loading = false;
    this.cd.detectChanges();
    this.commonService.notify('error', error.message);
  }

  closeDrawer(isRecordChange = false) {
    const drawer = this.drawerService.getDrawerRef(DRAWER_IDS.DEFINE_BEHAVIOR);
    drawer?.close({ isRecordChange: isRecordChange });
  }

  control(ctrl: string): FormControl {
    return this.form.get(ctrl) as FormControl;
  }

  openExampleofGoals() {
    this.isGoalExampleModelVisible = true;
  }

  async print() {
    const factory = this.viewContainerRef.createComponent(
      PrintCaseBehaviorComponent
    );

    factory.instance.printData = null;
    const componentElement = factory.location.nativeElement;
    componentElement.style.display = 'none';
    this.isPrintLoading = true;
    await Promise.all([
      this.orgService
        .getOrganization(this.caseParams?.case?.student.organizationId)
        .toPromise(),
    ])
      .then(async ([orgRes]: [OrganizationModel | undefined]) => {
        let base64Image = '';
        if (orgRes && orgRes.logoUrl) {
          base64Image = await this.commonService.getBase64ImageFromUrl(
            orgRes.logoUrl
          );
        } else {
          base64Image = await this.commonService.getBase64ImageFromUrl(
            '/assets/images/logo.png'
          );
        }
        factory.instance.behaviorData = {
          caseParams: this.caseParams,
          logo: base64Image,
          orgDetail: orgRes,
          form: {
            behaviorCategoryId: _.get(
              _.find(this.behaviorCategoryList, {
                id: this.control('behaviorCategoryId').value,
              }),
              'name',
              null
            ),
            caseBehaviorTypeIds:
              this.control('caseBehaviorTypeIds').value.length > 0
                ? _.map(
                    _.filter(this.behaviourSubCategoryList, item =>
                      _.includes(
                        this.control('caseBehaviorTypeIds').value,
                        item.id
                      )
                    ),
                    'name'
                  )
                : [],
            operationalDefinition: this.control('operationalDefinition').value,
            behaviourFrequencyId: this.control('behaviourFrequencyId').value
              ? _.get(
                  _.find(this.behaviourFrequencyList, {
                    id: this.control('behaviourFrequencyId').value,
                  }),
                  'name',
                  null
                )
              : null,
            behaviourFrequencyCount: this.control('behaviourFrequencyCount')
              .value,
            isDurationRequired: this.control('isDurationRequired').value,
            duration: this.control('duration').value,
            behaviourIntensityId: this.control('behaviourIntensityId').value
              ? _.get(
                  _.find(this.behaviourIntensityList, {
                    id: this.control('behaviourIntensityId').value,
                  }),
                  'name',
                  null
                )
              : null,
            goals: this.control('goals').value,
            draft: [true],
            draftStep: [1],
            caseBehaviorAntecedentIds:
              this.control('caseBehaviorAntecedentIds').value.length > 0
                ? _.map(
                    _.filter(this.behaviourAntecedentList, item =>
                      _.includes(
                        this.control('caseBehaviorAntecedentIds').value,
                        item.id
                      )
                    ),
                    'name'
                  )
                : [],
            caseBehaviorEnvironmentIds:
              this.control('caseBehaviorEnvironmentIds').value.length > 0
                ? _.map(
                    _.filter(this.behaviourEnvironmentList, item =>
                      _.includes(
                        this.control('caseBehaviorEnvironmentIds').value,
                        item.id
                      )
                    ),
                    'name'
                  )
                : [],
            caseBehaviorConsequenceIds:
              this.control('caseBehaviorConsequenceIds').value.length > 0
                ? _.map(
                    _.filter(this.behaviorConsequenceList, item =>
                      _.includes(
                        this.control('caseBehaviorConsequenceIds').value,
                        item.id
                      )
                    ),
                    'name'
                  )
                : [],
            caseBehaviorSettingEventIds:
              this.control('caseBehaviorSettingEventIds').value.length > 0
                ? _.map(
                    _.filter(this.behaviourSettingEventList, item =>
                      _.includes(
                        this.control('caseBehaviorSettingEventIds').value,
                        item.id
                      )
                    ),
                    'name'
                  )
                : [],
            caseBehaviorTypeDescriptions:
              this.control('caseBehaviorTypeDescriptions').value.length > 0
                ? _.map(
                    this.control('caseBehaviorTypeDescriptions').value,
                    'description'
                  )
                : [],
            caseBehaviorConsequenceDescription:
              this.control('caseBehaviorConsequenceDescription').value.length >
              0
                ? _.map(
                    this.control('caseBehaviorConsequenceDescription').value,
                    'description'
                  )
                : [],
            caseBehaviorAntecedentDescription:
              this.control('caseBehaviorAntecedentDescription').value.length > 0
                ? _.map(
                    this.control('caseBehaviorAntecedentDescription').value,
                    'description'
                  )
                : [],
            caseBehaviorCustomQuestionAnswer: _.chain(
              this.control('caseBehaviorCustomQuestionAnswer').value
            )
              .map(item => {
                const title = this.getquestionTitleById(item.customQuestionId);
                return title !== '' ? { ...item, customQuestion: title } : null;
              })
              .filter(Boolean)
              .value(),
          },
        };

        this.cd.detectChanges();
        this.waitForInitialization(factory).then(async () => {
          await factory.instance.printPDF();
          factory.destroy();
          this.isPrintLoading = false;
          this.cd.markForCheck();
        });
      })
      .catch(e => console.error(e))
      .finally(() => {
        this.isPrintLoading = false;
        this.cd.markForCheck();
      });
  }

  get intensityName() {
    return this.behaviourIntensityList.find(
      q => q.id === this.form.controls['behaviourIntensityId'].value
    )?.name;
  }

  get isWarningModalVisible(): boolean {
    return (
      this.isVisible &&
      (this.intensityName === 'Moderate' || this.intensityName === 'Major')
    );
  }

  getquestionTitleById(id: number) {
    return (
      this.customQuestionList.find(
        q =>
          q.id === id &&
          q.questionForReportTypeId !== CustomQuestionReportType.BIP
      )?.customQuestion ?? ''
    );
  }

  getCategoryNameById(id: number) {
    return this.behaviorCategoryList.find(q => q.id === id)?.name ?? '';
  }

  getFrequencyNameById(id: number | null): string {
    if (!id) return '';
    const frequency = this.behaviourFrequencyList.find(f => f.id === id);
    return frequency ? this.getBehaviorFrequencyLabel(frequency.name) : '';
  }

  getIntensityNameById(id: number | null): string {
    if (!id) return '';
    return this.behaviourIntensityList.find(i => i.id === id)?.name ?? '';
  }

  getEnvironmentNameById(id: number): string {
    return this.behaviourEnvironmentList.find(e => e.id === id)?.name ?? '';
  }

  getSettingEventNameById(id: number): string {
    return this.behaviourSettingEventList.find(s => s.id === id)?.name ?? '';
  }

  waitForInitialization(factory: ComponentRef<PrintCaseBehaviorComponent>) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }
}
